import { StreamWithChatRate } from '../types';

/**
 * Thermal tiers for the heat ratio (last 10s chat rate vs 5-min average).
 * One shared ramp everywhere: cold → warm (2x) → hot (3x) → blazing (5x).
 */
export type HeatTier = 'cold' | 'warm' | 'hot' | 'blazing';

export const getHeatRatio = (s: Pick<StreamWithChatRate, 'chatRate10s' | 'chatRate5m'>): number =>
  s.chatRate5m && s.chatRate5m > 0 ? (s.chatRate10s || 0) / s.chatRate5m : 0;

/** Seconds of observation required before a heat ratio means anything.
 *  Below this the 5m average's short denominator inflates every channel
 *  to roughly the same fake spike (30s window ÷ elapsed). */
export const HEAT_BASELINE_S = 30;

export const isHeatReady = (s: Pick<StreamWithChatRate, 'sampleSeconds'>): boolean =>
  (s.sampleSeconds ?? 0) >= HEAT_BASELINE_S;

export const getHeatTier = (ratio: number): HeatTier => {
  if (ratio >= 5) return 'blazing';
  if (ratio >= 3) return 'hot';
  if (ratio >= 2) return 'warm';
  return 'cold';
};

/** 1234 → "1.2K", 1200000 → "1.2M" — the way Twitch shows viewer counts */
export const formatCompact = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 100_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `${n}`;
};

/** "2026-07-05T01:23:45Z" → "2h 41m" */
export const formatUptime = (startedAt: string): string | null => {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return null;
  const mins = Math.max(0, Math.floor((Date.now() - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
